import { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { useTimeBoundedFeatureFlagEvaluator } from 'components/helpers/hooks';

export const AVAILABLE_SOON_NOTIFY_ME_FLAG = 'growth-26-09-available-soon-notify-me-cta';

// Variant serves GrowthBook booleans as 'true'; the localStorage QA override
// normalizes to 'on'.
const flagIsOn = (variant: string) => variant === 'true' || variant === 'on';

/**
 * The notify-me card's own gate, separate from the segment flag so the card
 * can roll back without touching the coming-soon inventory filter. Fails
 * closed: a flag read that errors or hangs renders no card.
 *
 * Takes `enabled` so callers skip the read entirely when the card cannot
 * render (most PDPs are not coming-soon).
 */
export const useAvailableSoonNotifyMeEnabled = (enabled: boolean): boolean => {
  const { authentication } = useAuth();
  const customerUuid =
    authentication.state === 'authenticated' ? (authentication.user?.customerUuid ?? '') : '';
  const evaluateFlag = useTimeBoundedFeatureFlagEvaluator(flagIsOn);
  const [evaluated, setEvaluated] = useState<{ customerUuid: string; isEnabled: boolean } | null>(
    null,
  );
  const [lastEnabled, setLastEnabled] = useState(enabled);

  if (enabled !== lastEnabled) {
    // Render-phase reset (the sanctioned alternative to setState-in-effect):
    // a disable→re-enable cycle, e.g. a client-side PDP→PDP round trip, must
    // not serve the previous verdict while the fresh read is in flight.
    setLastEnabled(enabled);
    setEvaluated(null);
  }

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let isActive = true;

    void evaluateFlag(AVAILABLE_SOON_NOTIFY_ME_FLAG)
      .then((isEnabled) => {
        if (isActive) {
          setEvaluated({ customerUuid, isEnabled });
        }
      })
      .catch(() => {
        if (isActive) {
          setEvaluated({ customerUuid, isEnabled: false });
        }
      });

    return () => {
      isActive = false;
    };
  }, [evaluateFlag, customerUuid, enabled]);

  if (!enabled) {
    return false;
  }

  // Tied to the customer it was evaluated for; a mid-session login/logout
  // re-evaluates rather than carrying the last identity's result. Anonymous
  // and uuid-less sessions share the '' key — acceptable for a flag with no
  // per-user targeting.
  return evaluated?.customerUuid === customerUuid && evaluated.isEnabled;
};
